'use client'

import React, { useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ArrowLeft,
  BadgeCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Crown,
  Image as ImageIcon,
  Loader2,
  Shield,
  Sparkles,
  Upload,
  Wand2,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import {
  useTaskFlowStore,
  type TaskFlowPhase,
  type TaskFlowRequest,
} from '@/lib/task-flow/taskFlowStore'
import Cropper from 'react-easy-crop'

type PortraitMode = 'express' | 'pro'
type PortraitStyle = 'professional' | 'corporate' | 'premium'
type PortraitPosePreset = 'neutral_standing' | 'arms_crossed' | 'relaxed_confident'
type PortraitClothingPreset =
  | 'smart_casual'
  | 'business'
  | 'premium_black'
  | 'creator_modern'

type StepId =
  | 'choose_mode'
  | 'upload_image'
  | 'configure_express'
  | 'generate_express'
  | 'result_express'
  | 'pro_intro'

type PortraitResult = {
  portrait_id?: number
  image_url: string | null
  source_image_url?: string | null
  is_primary?: number | null
  provider?: string | null
  model?: string | null
  status?: string
  date_added?: string
}

const EXPRESS_STEPS: StepId[] = [
  'choose_mode',
  'upload_image',
  'configure_express',
  'generate_express',
  'result_express',
]

const MODE_OPTIONS = [
  {
    id: 'express' as const,
    title: 'Express',
    eyebrow: 'Rápido y directo',
    description:
      'Sube una foto, elige estilo, pose y ropa, y genera tu retrato profesional en pocos pasos.',
    icon: Shield,
  },
  {
    id: 'pro' as const,
    title: 'Pro',
    eyebrow: 'Consistencia avanzada',
    description:
      'Entrena tu identidad visual con varias fotos para producir retratos más consistentes y premium.',
    icon: Crown,
  },
]

const STYLE_OPTIONS: Array<{
  id: PortraitStyle
  title: string
  description: string
}> = [
  {
    id: 'professional',
    title: 'Profesional',
    description: 'Equilibrado, limpio y usable para branding.',
  },
  {
    id: 'corporate',
    title: 'Corporate',
    description: 'Más ejecutivo, autoridad y presencia.',
  },
  {
    id: 'premium',
    title: 'Premium',
    description: 'Más editorial, luxury y aspiracional.',
  },
]

const POSE_OPTIONS: Array<{
  id: PortraitPosePreset
  title: string
  description: string
}> = [
  {
    id: 'neutral_standing',
    title: 'De pie neutro',
    description: 'Postura limpia y natural.',
  },
  {
    id: 'arms_crossed',
    title: 'Brazos cruzados',
    description: 'Más autoridad y presencia.',
  },
  {
    id: 'relaxed_confident',
    title: 'Relajado',
    description: 'Seguro, natural y moderno.',
  },
]

const CLOTHING_OPTIONS: Array<{
  id: PortraitClothingPreset
  title: string
  description: string
}> = [
  {
    id: 'smart_casual',
    title: 'Smart casual',
    description: 'Moderno y limpio.',
  },
  {
    id: 'business',
    title: 'Business',
    description: 'Ejecutivo / corporativo.',
  },
  {
    id: 'premium_black',
    title: 'Premium black',
    description: 'Sobrio y elegante.',
  },
  {
    id: 'creator_modern',
    title: 'Creator',
    description: 'Marca personal moderna.',
  },
]

const EXPRESS_MODE_ONLY = true
const ENABLE_PORTRAIT_DEMO = false
const PORTRAIT_DEMO_DELAY_MS = 5000
const PORTRAIT_DEMO_IMAGE_URL = 'https://v3b.fal.media/files/b/0a9665de/9APO4G3vNE15YIJU0cbqA_fea7a8afb55d4727bec2cc8b9af0ff28.png'

function getCroppedDataUrl(
  imageSrc: string,
  cropPixels: { x: number; y: number; width: number; height: number }
) {
  return new Promise<string>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = cropPixels.width
      canvas.height = cropPixels.height

      const ctx = canvas.getContext('2d')
      if (!ctx) return reject(new Error('No canvas context'))

      ctx.drawImage(
        img,
        cropPixels.x,
        cropPixels.y,
        cropPixels.width,
        cropPixels.height,
        0,
        0,
        cropPixels.width,
        cropPixels.height
      )

      resolve(canvas.toDataURL('image/jpeg', 0.92))
    }

    img.onerror = () => reject(new Error('No se pudo cargar la imagen para recorte'))
    img.src = imageSrc
  })
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(' ')
}

function FlowShell({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-none border border-cyan-400/10 bg-white text-slate-900 dark:bg-[#030712] dark:text-cyan-50 md:rounded-[32px]">
      {children}
    </div>
  )
}

function StepCard({
  eyebrow,
  title,
  subtitle,
  children,
}: {
  eyebrow: string
  title: string
  subtitle: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-[32px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(248,250,252,0.92))] p-6 shadow-[0_20px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-cyan-400/14 dark:bg-[linear-gradient(180deg,rgba(10,18,34,0.96),rgba(5,11,22,0.92))] dark:shadow-[0_0_0_1px_rgba(34,211,238,0.03),0_20px_80px_rgba(0,0,0,0.35)]">
      <div className="text-[11px] uppercase tracking-[0.28em] text-cyan-700/70 dark:text-cyan-300/58">
        {eyebrow}
      </div>

      <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-900 dark:text-cyan-50">
        {title}
      </div>

      <div className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-cyan-100/58">
        {subtitle}
      </div>

      <div className="mt-6">{children}</div>
    </div>
  )
}

function InfoBox({
  label,
  value,
}: {
  label: string
  value: string
}) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/70 px-4 py-3 dark:border-cyan-400/10 dark:bg-cyan-400/[0.04]">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-cyan-300/55">
        {label}
      </div>
      <div className="mt-2 text-sm text-slate-900 dark:text-cyan-50">{value || '—'}</div>
    </div>
  )
}

function StepProgress({
  current,
  total,
}: {
  current: number
  total: number
}) {
  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.24em] text-cyan-700/70 dark:text-cyan-300/60">
          Portrait Flow
        </div>
        <div className="font-mono text-[11px] text-slate-500 dark:text-cyan-200/60">
          {current}/{total}
        </div>
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-cyan-500/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-cyan-500 via-sky-400 to-violet-400 transition-all duration-300"
          style={{ width: `${(current / total) * 100}%` }}
        />
      </div>
    </div>
  )
}

function OptionGrid({
  options,
  value,
  onChange,
}: {
  options: Array<{
    id: string
    title: string
    description: string
    icon?: React.ComponentType<{ className?: string }>
    eyebrow?: string
  }>
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {options.map((option) => {
        const active = value === option.id
        const Icon = option.icon

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChange(option.id)}
            className={cn(
              'rounded-[26px] border p-5 text-left transition',
              active
                ? 'border-2 border-slate-900 bg-slate-100 shadow-[0_0_0_1px_rgba(15,23,42,0.06)] dark:border-cyan-300/60 dark:bg-cyan-300/14'
                : 'border border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50 dark:border-cyan-400/14 dark:bg-cyan-400/[0.04] dark:hover:border-cyan-300/25 dark:hover:bg-cyan-400/[0.07]'
            )}
          >
            {option.eyebrow ? (
              <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-700/70 dark:text-cyan-300/50">
                {option.eyebrow}
              </div>
            ) : null}

            <div className="mt-1 flex items-center gap-3">
              {Icon ? (
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-400/20 bg-cyan-400/10 text-cyan-700 dark:text-cyan-300">
                  <Icon className="h-5 w-5" />
                </div>
              ) : null}

              <div className="text-lg font-semibold text-slate-900 dark:text-cyan-50">
                {option.title}
              </div>
            </div>

            <div className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-cyan-100/56">
              {option.description}
            </div>
          </button>
        )
      })}
    </div>
  )
}

export default function SynCreatePortraitFlow({
  request,
}: {
  request: TaskFlowRequest
  phase: TaskFlowPhase
}) {
  const router = useRouter()
  const resetTaskFlow = useTaskFlowStore((s) => s.resetTaskFlow)

  const sellerId = Number(request.payload?.sellerId || 0)
  const userId = Number(request.payload?.userId || 0)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [mode, setMode] = useState<PortraitMode | ''>(
    EXPRESS_MODE_ONLY ? 'express' : ''
  )

  const [step, setStep] = useState<StepId>(
    EXPRESS_MODE_ONLY ? 'upload_image' : 'choose_mode'
  )
  const [sourceImage, setSourceImage] = useState<string | null>(null)
  const [portraitStyle, setPortraitStyle] = useState<PortraitStyle>('professional')
  const [posePreset, setPosePreset] = useState<PortraitPosePreset>('neutral_standing')
  const [clothingPreset, setClothingPreset] =
    useState<PortraitClothingPreset>('smart_casual')

  const [busy, setBusy] = useState(false)
  const [portraitResult, setPortraitResult] = useState<PortraitResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [cropOpen, setCropOpen] = useState(false)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null)

  const currentExpressStep = useMemo(() => {
    const steps = EXPRESS_MODE_ONLY
      ? EXPRESS_STEPS.filter((s) => s !== 'choose_mode')
      : EXPRESS_STEPS

    const index = steps.findIndex((s) => s === step)
    return index >= 0 ? index + 1 : 1
  }, [step])

  function goToCreatorStudioPortrait(tab: 'portrait' | 'flyer' = 'portrait') {
    resetTaskFlow()
    router.push(`/creator-studio?tab=${tab}`)
  }

  function handleModeSelect(nextMode: PortraitMode) {
    setMode(nextMode)
    setError(null)

    if (nextMode === 'express') {
      setStep('upload_image')
      return
    }

    setStep('pro_intro')
  }

  function handleLocalImageUpload(file: File) {
    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten imágenes')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Máximo 10MB')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      setSourceImage(reader.result as string)
      setError(null)
    }
    reader.readAsDataURL(file)
  }

  async function handleGenerateExpress() {
    if (!sourceImage) {
      setError('Sube una imagen primero')
      return
    }

    try {
      setBusy(true)
      setError(null)

      if (ENABLE_PORTRAIT_DEMO) {
        await wait(PORTRAIT_DEMO_DELAY_MS)

        setPortraitResult({
          portrait_id: 999999,
          image_url: PORTRAIT_DEMO_IMAGE_URL,
          source_image_url: sourceImage,
          is_primary: 1,
          provider: 'demo',
          model: 'demo_portrait_mock',
          status: 'ready',
          date_added: new Date().toISOString(),
        })

        setStep('result_express')
        return
      }

      const res = await fetch('/api/creator-studio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tab: 'portrait',
          inputs: {
            imageUrl: sourceImage,
            style: portraitStyle,
            posePreset,
            clothingPreset,
            makePrimary: 1,
            source: 'task_flow_portrait',
            userId,
            sellerId,
          },
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo generar el retrato')
      }

      const portrait = json?.generation?.portrait || null
      const resultJson = json?.generation?.result_json || null

      setPortraitResult({
        portrait_id: Number(portrait?.portrait_id || 0) || undefined,
        image_url: portrait?.image_url || resultJson?.image_url || null,
        source_image_url: portrait?.source_image_url || null,
        is_primary: portrait?.is_primary ?? null,
        provider: json?.generation?.provider || null,
        model: json?.generation?.model || null,
        status: portrait?.status || 'ready',
        date_added: json?.generation?.date_added,
      })

      setStep('result_express')
    } catch (e: any) {
      setError(e?.message || 'Error generando retrato')
      setStep('generate_express')
    } finally {
      setBusy(false)
    }
  }

  async function usePortraitAsProfileImage(finalImageUrl?: string) {
    if (!portraitResult?.image_url) {
      setError('No hay imagen generada')
      return
    }

    try {
      setBusy(true)
      setError(null)

      const res = await fetch('/api/creator-studio/portrait/use-as-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sellerId,
          imageUrl: finalImageUrl || portraitResult.image_url,
          portraitId: portraitResult.portrait_id || null,
        }),
      })

      const json = await res.json().catch(() => null)

      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo usar como imagen de perfil')
      }

      await completeOnboardingToolStep({
        source: 'portrait_flow',
        toolKey: 'portrait_creator',
        portraitId: portraitResult?.portrait_id || null,
        profileImage: json.avatar,
        status: portraitResult?.status || null,
      })

      window.dispatchEvent(
        new CustomEvent('profile-avatar-updated', {
          detail: {
            avatar: json.avatar,
          },
        })
      )

      window.dispatchEvent(new CustomEvent('dashboard-onboarding-refresh'))

      resetTaskFlow()
    } catch (e: any) {
      setError(e?.message || 'Error guardando imagen de perfil')
    } finally {
      setBusy(false)
    }
  }

  async function confirmCropAndUseAsProfile() {
    if (!portraitResult?.image_url || !croppedAreaPixels) {
      setError('No se pudo preparar el recorte')
      return
    }

    try {
      setBusy(true)
      setError(null)

      const croppedDataUrl = await getCroppedDataUrl(
        portraitResult.image_url,
        croppedAreaPixels
      )

      await usePortraitAsProfileImage(croppedDataUrl)

      setCropOpen(false)
    } catch (e: any) {
      setError(e?.message || 'Error recortando imagen')
    } finally {
      setBusy(false)
    }
  }

  async function completeOnboardingToolStep(meta?: Record<string, any>) {
    const moduleId = Number(request.payload?.onboardingStepId || 0)
    const sellerId = Number(request.payload?.sellerId || 0)

    if (!moduleId || !sellerId) return

    try {
        const res = await fetch('/api/dashboard/onboarding/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            sellerId,
            moduleId,
            meta: meta || null,
        }),
        })

        const json = await res.json().catch(() => null)

        if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'No se pudo completar el step')
        }
    } catch (err) {
        console.error('[SynCreatePortraitFlow] completeOnboardingToolStep error:', err)
    }
  }

  function goNext() {
    if (step === 'upload_image') {
      if (!sourceImage) {
        setError('Sube una imagen primero')
        return
      }
      setStep('configure_express')
      return
    }

    if (step === 'configure_express') {
      setStep('generate_express')
    }
  }

  function goBack() {
    setError(null)

    if (step === 'upload_image') {
      setStep('choose_mode')
      return
    }

    if (step === 'configure_express') {
      setStep('upload_image')
      return
    }

    if (step === 'generate_express') {
      setStep('configure_express')
      return
    }

    if (step === 'result_express') {
      setStep('generate_express')
      return
    }

    if (step === 'pro_intro') {
      setStep('choose_mode')
    }
  }

  return (
    <FlowShell>
      <div className="shrink-0 border-b border-slate-200 px-6 py-5 dark:border-cyan-400/10">
        <div className="text-[11px] uppercase tracking-[0.28em] text-cyan-700/70 dark:text-cyan-300/60">
          SYN Portrait Runtime
        </div>
        <div className="mt-2 text-2xl font-semibold">Crear retrato profesional</div>
        <div className="mt-2 text-sm text-slate-600 dark:text-cyan-100/60">
          Un flujo guiado, limpio y cinematográfico para construir tu imagen profesional.
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-6 pb-10">
        {mode === 'express' && step !== 'choose_mode' && step !== 'pro_intro' ? (
          <StepProgress
            current={currentExpressStep}
            total={EXPRESS_MODE_ONLY ? EXPRESS_STEPS.length - 1 : EXPRESS_STEPS.length}
          />
        ) : null}

        {error ? (
          <div className="mb-5 rounded-2xl border border-rose-500/20 bg-rose-500/8 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
            {error}
          </div>
        ) : null}

        <AnimatePresence mode="wait">
          {!EXPRESS_MODE_ONLY && step === 'choose_mode' && (
            <motion.div
              key="choose_mode"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
            >
              <StepCard
                eyebrow="Modo"
                title="Elige cómo quieres crear tu retrato"
                subtitle="Puedes ir por una ruta rápida y directa, o preparar una experiencia más avanzada para identidad visual consistente."
              >
                <OptionGrid
                  options={MODE_OPTIONS.map((item) => ({
                    id: item.id,
                    title: item.title,
                    description: item.description,
                    icon: item.icon,
                    eyebrow: item.eyebrow,
                  }))}
                  value={mode}
                  onChange={(value) => handleModeSelect(value as PortraitMode)}
                />
              </StepCard>
            </motion.div>
          )}

          {step === 'upload_image' && (
            <motion.div
              key="upload_image"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
              className="flex min-h-[70vh] flex-col"
            >
              <StepCard
                eyebrow="Express"
                title="Comienza por subir tu imagen"
                subtitle="Usa una foto clara, con buena luz y rostro visible. Eso mejora muchísimo el resultado final."
              >
                <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="overflow-hidden rounded-[28px] border border-dashed border-slate-300 bg-slate-50 text-left transition hover:border-cyan-400/40 dark:border-cyan-400/14 dark:bg-cyan-400/[0.04] dark:hover:border-cyan-300/25"
                  >
                    <div className="relative aspect-[4/5] w-full">
                      {sourceImage ? (
                        <img
                          src={sourceImage}
                          alt="Fuente"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-slate-500 dark:text-cyan-100/45">
                          <Upload className="h-10 w-10" />
                          <div className="text-sm font-medium">Subir imagen</div>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-slate-200 px-4 py-3 text-xs text-slate-500 dark:border-cyan-400/10 dark:text-cyan-100/45">
                      PNG o JPG · máximo 10MB
                    </div>
                  </button>

                  <div className="space-y-4">
                    <InfoBox label="Recomendación" value="Rostro claro, buena luz, sin filtros fuertes." />
                    <InfoBox label="Meta" value="Crear una base premium para branding, perfiles y flyers." />
                    <InfoBox label="Resultado" value="Retrato profesional con pose, ropa y estilo guiados." />
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleLocalImageUpload(file)
                  }}
                />
              </StepCard>

              <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4 dark:border-cyan-400/10">
                <button
                  type="button"
                  onClick={goBack}
                  className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-5 py-3 text-sm text-cyan-700 dark:text-cyan-200"
                >
                  Atrás
                </button>

                <button
                  type="button"
                  onClick={goNext}
                  disabled={!sourceImage}
                  className="rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 disabled:opacity-40"
                >
                  Continuar
                </button>
              </div>
            </motion.div>
          )}

          {step === 'configure_express' && (
            <motion.div
              key="configure_express"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
              className="flex min-h-[70vh] flex-col"
            >
              <StepCard
                eyebrow="Express"
                title="Define el estilo visual del retrato"
                subtitle="Aquí decides cómo se debe ver tu imagen final: tono, pose y vestuario."
              >
                <div className="space-y-6">
                  <div>
                    <div className="mb-3 text-[11px] uppercase tracking-[0.18em] text-cyan-700/70 dark:text-cyan-300/55">
                      Estilo
                    </div>
                    <OptionGrid
                      options={STYLE_OPTIONS.map((opt) => ({
                        id: opt.id,
                        title: opt.title,
                        description: opt.description,
                      }))}
                      value={portraitStyle}
                      onChange={(value) => setPortraitStyle(value as PortraitStyle)}
                    />
                  </div>

                  <div>
                    <div className="mb-3 text-[11px] uppercase tracking-[0.18em] text-cyan-700/70 dark:text-cyan-300/55">
                      Pose
                    </div>
                    <OptionGrid
                      options={POSE_OPTIONS.map((opt) => ({
                        id: opt.id,
                        title: opt.title,
                        description: opt.description,
                      }))}
                      value={posePreset}
                      onChange={(value) => setPosePreset(value as PortraitPosePreset)}
                    />
                  </div>

                  <div>
                    <div className="mb-3 text-[11px] uppercase tracking-[0.18em] text-cyan-700/70 dark:text-cyan-300/55">
                      Clothing
                    </div>
                    <OptionGrid
                      options={CLOTHING_OPTIONS.map((opt) => ({
                        id: opt.id,
                        title: opt.title,
                        description: opt.description,
                      }))}
                      value={clothingPreset}
                      onChange={(value) => setClothingPreset(value as PortraitClothingPreset)}
                    />
                  </div>
                </div>
              </StepCard>

              <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4 dark:border-cyan-400/10">
                <button
                  type="button"
                  onClick={goBack}
                  className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-5 py-3 text-sm text-cyan-700 dark:text-cyan-200"
                >
                  Atrás
                </button>

                <button
                  type="button"
                  onClick={goNext}
                  className="rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950"
                >
                  Continuar
                </button>
              </div>
            </motion.div>
          )}

          {step === 'generate_express' && (
            <motion.div
              key="generate_express"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
              className="flex min-h-[70vh] flex-col"
            >
              <StepCard
                eyebrow="Express"
                title="Todo listo para generar"
                subtitle="SYN ya tiene la foto base y la dirección visual. En el siguiente paso creará tu retrato profesional."
              >
                <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
                  <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 dark:border-cyan-400/14 dark:bg-[#07101d]">
                    <div className="aspect-[4/5] w-full bg-slate-100 dark:bg-cyan-400/10">
                      {sourceImage ? (
                        <img
                          src={sourceImage}
                          alt="Preview"
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                  </div>

                  <div className="grid gap-3">
                    <InfoBox
                      label="Modo"
                      value={mode === 'express' ? 'Express' : 'Pro'}
                    />
                    <InfoBox
                      label="Estilo"
                      value={STYLE_OPTIONS.find((x) => x.id === portraitStyle)?.title || ''}
                    />
                    <InfoBox
                      label="Pose"
                      value={POSE_OPTIONS.find((x) => x.id === posePreset)?.title || ''}
                    />
                    <InfoBox
                      label="Ropa"
                      value={CLOTHING_OPTIONS.find((x) => x.id === clothingPreset)?.title || ''}
                    />
                  </div>
                </div>
              </StepCard>

              <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-4 dark:border-cyan-400/10">
                <button
                  type="button"
                  onClick={goBack}
                  className="rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-5 py-3 text-sm text-cyan-700 dark:text-cyan-200"
                >
                  Atrás
                </button>

                <button
                  type="button"
                  onClick={handleGenerateExpress}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {busy ? 'Generando...' : 'Crear retrato'}
                </button>
              </div>
            </motion.div>
          )}

          {step === 'result_express' && (
            <motion.div
              key="result_express"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
            >
              <StepCard
                eyebrow="Resultado"
                title="Tu retrato profesional está listo"
                subtitle="Ya puedes seguir creando, abrir Creator Studio o pasar directamente a generar un flyer."
              >
                <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
                  <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 dark:border-cyan-400/14 dark:bg-[#07101d]">
                    <div className="relative aspect-[4/5] w-full">
                      {portraitResult?.image_url ? (
                        <img
                          src={portraitResult.image_url}
                          alt="Portrait result"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-slate-500 dark:text-cyan-100/45">
                          <ImageIcon className="h-10 w-10" />
                        </div>
                      )}

                      <div className="absolute left-3 top-3 rounded-full border border-emerald-400/25 bg-emerald-400/15 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-emerald-100">
                        Listo
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm text-cyan-700 dark:text-cyan-200">
                      <CheckCircle2 className="h-4 w-4" />
                      Retrato creado correctamente
                    </div>

                    <InfoBox label="Provider" value={portraitResult?.provider || 'fal'} />
                    <InfoBox label="Modelo" value={portraitResult?.model || '—'} />
                    <InfoBox label="Status" value={portraitResult?.status || 'ready'} />
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      setCropOpen(true)
                    }}
                    disabled={!portraitResult?.image_url || busy}
                    className="inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950 disabled:opacity-40"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
                    {busy ? 'Guardando...' : 'Utilizar como imagen de perfil'}
                  </button>
                </div>

                <div className="mt-8 flex flex-wrap gap-3 hidden">
                  <button
                    type="button"
                    onClick={async () => {
                        await completeOnboardingToolStep({
                        source: 'portrait_flow',
                        toolKey: 'portrait_creator',
                        portraitId: portraitResult?.portrait_id || null,
                        status: portraitResult?.status || null,
                        })
                        goToCreatorStudioPortrait('portrait')
                    }}
                    className="inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950"
                    >
                    <BadgeCheck className="h-4 w-4" />
                    Abrir en Portrait
                  </button>

                  <button
                    type="button"
                    onClick={async () => {
                        await completeOnboardingToolStep({
                        source: 'portrait_flow',
                        toolKey: 'portrait_creator',
                        portraitId: portraitResult?.portrait_id || null,
                        status: portraitResult?.status || null,
                        })
                        goToCreatorStudioPortrait('flyer')
                    }}
                    className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-5 py-3 text-sm text-cyan-700 dark:text-cyan-200"
                  >
                    <Wand2 className="h-4 w-4" />
                    Continuar con flyer
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep('choose_mode')
                      setMode('')
                      setSourceImage(null)
                      setPortraitResult(null)
                      setError(null)
                    }}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm text-slate-700 dark:border-cyan-400/20 dark:bg-cyan-400/10 dark:text-cyan-200"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Crear otro
                  </button>
                </div>
              </StepCard>
            </motion.div>
          )}

          {step === 'pro_intro' && (
            <motion.div
              key="pro_intro"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.22 }}
              className="flex min-h-[70vh] flex-col"
            >
              <StepCard
                eyebrow="Pro"
                title="La ruta Pro entra a identidad visual avanzada"
                subtitle="Aquí entrenarás un modelo con varias fotos para producir retratos más consistentes. Como tu entrenamiento Pro aún depende del endpoint real, te voy a llevar limpio al estudio para seguir ahí sin fricción."
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <InfoBox label="Dataset recomendado" value="10–20 fotos reales y variadas" />
                  <InfoBox label="Objetivo" value="Consistencia premium entre retratos y materiales visuales" />
                  <InfoBox label="Siguiente paso" value="Abrir Creator Studio en Portrait Pro" />
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => goToCreatorStudioPortrait('portrait')}
                    className="inline-flex items-center gap-2 rounded-2xl bg-cyan-400 px-5 py-3 text-sm font-semibold text-slate-950"
                  >
                    <Crown className="h-4 w-4" />
                    Continuar con Pro
                  </button>

                  <button
                    type="button"
                    onClick={goBack}
                    className="inline-flex items-center gap-2 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-5 py-3 text-sm text-cyan-700 dark:text-cyan-200"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Cambiar a Express
                  </button>
                </div>
              </StepCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {cropOpen && portraitResult?.image_url ? (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md">
            <div className="w-full max-w-2xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-cyan-400/20 dark:bg-[#07101d]">
              <div className="border-b border-slate-200 px-5 py-4 dark:border-cyan-400/10">
                <div className="text-lg font-semibold text-slate-900 dark:text-cyan-50">
                  Recortar foto de perfil
                </div>
                <div className="mt-1 text-sm text-slate-500 dark:text-cyan-100/55">
                  Ajusta el encuadre cuadrado antes de usarla como avatar.
                </div>
              </div>

              <div className="relative h-[420px] w-full bg-slate-100 dark:bg-cyan-400/[0.04]">
                <Cropper
                  image={portraitResult.image_url}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-4 dark:border-cyan-400/10">
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full max-w-xs"
                />

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCropOpen(false)}
                    disabled={busy}
                    className="rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 dark:border-cyan-400/20 dark:bg-cyan-400/10 dark:text-cyan-200"
                  >
                    Cancelar
                  </button>

                  <button
                    type="button"
                    onClick={confirmCropAndUseAsProfile}
                    disabled={busy || !croppedAreaPixels}
                    className="rounded-2xl bg-cyan-400 px-5 py-2 text-sm font-semibold text-slate-950 disabled:opacity-40"
                  >
                    {busy ? 'Guardando...' : 'Guardar foto'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
    </FlowShell>
  )
}